import { ActionImpl, KBarResults, useMatches } from 'kbar';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CommandBarResultsItem } from './CommandBarResultsItem';
import { CommandBarEntityAction, RenderParams } from './commandBarTypes';
import { UserResultItem } from './UserResultItem';

/**
 * Actions registered only to bind a key — they have no destination of their own,
 * so listing them as results would offer the user a dead entry.
 */
const HiddenCommandBarActionIds = {
  'escape-shortcut': true,
  'search-shortcut': true,
  shortcutSidebar: true,
};

/**
 * check the type of the result and return Id accordingly
 * @param result the result from Kbar useMatches
 * @returns
 */
const getResultItemId = (result: ActionImpl | string) => {
  if (typeof result === 'string' || result instanceof String) {
    return result as string;
  }
  return result.id;
};

function isEntityAction(
  action: ActionImpl,
): action is ActionImpl & CommandBarEntityAction {
  return action.parent === 'open-client';
}

export const CommandBarResult: React.FC = () => {
  const { t } = useTranslation();
  const { results, rootActionId } = useMatches();

  const filterBySectionName = (
    action: ActionImpl | string,
    sectionName: string,
  ) => action === sectionName || (action as ActionImpl).section === sectionName;

  /**
   * This holds commandbar actions search results
   * to be shown in the command bar menu.
   * It filters out the actions we don't want to show in the command bar.
   * Also it sort the actions by their section name.
   */
  const commandBarItems = React.useMemo(() => {
    const stagedResults = results.filter(
      (result) => !(getResultItemId(result) in HiddenCommandBarActionIds),
    );

    const navigationCommands = stagedResults.filter((item) =>
      filterBySectionName(item, 'Navigation'),
    );
    const generalCommands = stagedResults.filter((item) =>
      filterBySectionName(item, 'General'),
    );
    const helpCommands = stagedResults.filter((item) =>
      filterBySectionName(item, 'Help'),
    );
    const miscellaneousCommands = stagedResults.filter((item) =>
      filterBySectionName(item, 'Miscellaneous'),
    );
    const accountCommands = stagedResults.filter((item) =>
      filterBySectionName(item, 'Account'),
    );
    const otherCommands = stagedResults.filter(
      (item) =>
        // group actions objects without section prop
        typeof item !== 'string' && !(item instanceof String) && !item.section,
    );

    // Define section names to filter client sections
    const sectionNames = [
      `${t(($) => $.common.create)}...`,
      `${t(($) => $.actions.open_client)}...`,
      'Invite team member',
    ];

    // Extract client sections based on sectionNames
    const sectionsByName = otherCommands.filter(
      (item) =>
        typeof item !== 'string' &&
        item.name &&
        sectionNames.includes(item.name),
    );

    // extract all entities with the parent 'open-client'
    const allEntities = otherCommands.filter(
      (item): item is ActionImpl & CommandBarEntityAction =>
        typeof item !== 'string' &&
        !item.section &&
        item.parent === 'open-client',
    );

    const companyEntityCommands = allEntities.filter(
      (item) => item.additionalData?.entityType === 'company',
    );
    const clientEntityCommands = allEntities.filter(
      (item) => item.additionalData?.entityType !== 'company',
    );

    const entityCommands: Array<ActionImpl | string> = [];
    if (companyEntityCommands.length > 0) {
      entityCommands.push(
        t(($) => $.company, { count: 2 }),
        ...companyEntityCommands,
      );
    }
    if (clientEntityCommands.length > 0) {
      entityCommands.push(
        t(($) => $.client, { count: 2 }),
        ...clientEntityCommands,
      );
    }

    // Determine the final set of commands to display
    const clientCommands = allEntities.length
      ? [...sectionsByName, ...entityCommands]
      : otherCommands;

    // commands should appear in a specific order
    return [
      ...generalCommands,
      ...navigationCommands,
      ...helpCommands,
      ...miscellaneousCommands,
      ...accountCommands,
      ...clientCommands,
    ];
  }, [results, rootActionId]);

  const onItemRender = React.useCallback(
    (
      props: RenderParams<
        | string
        | (ActionImpl & {
            disabled?: boolean;
            callback?: React.MouseEventHandler<HTMLLIElement>;
          })
      >,
    ) => {
      // when item has parent id equal to open-client
      // it should render item with avatar
      const { item, active } = props;
      if (
        typeof item === 'object' &&
        'parent' in item &&
        isEntityAction(item)
      ) {
        return <UserResultItem item={item} active={active} />;
      }

      return <CommandBarResultsItem item={item} active={active} />;
    },
    [],
  );

  return <KBarResults items={commandBarItems} onRender={onItemRender} />;
};
